import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { isOrderNumberForOrderId, type OrderNumber, OrderNumberSchema } from "./order-number.js";
import {
  type PaidSaleReturnPaymentMethod,
  PaidSaleReturnPaymentMethodSchema,
} from "./paid-sale-return.js";

export const CONFIRMED_RECEIPT_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION =
  "CONFIRMED_RECEIPT_BUSINESS_METRIC_DETAIL_V1" as const;
export const CONFIRMED_RECEIPT_BUSINESS_METRIC_DETAIL_CAPACITY = 5_000;

export const ConfirmedReceiptNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^SK-[0-9]{8}-[0-9A-F]{12}$",
});
export type ConfirmedReceiptNumber = Static<typeof ConfirmedReceiptNumberSchema>;

export const ConfirmedReceiptPaymentStatusSchema = Type.Union([
  Type.Literal("CONFIRMED_LOCAL"),
  Type.Literal("CONFIRMED_CLOUD"),
  Type.Literal("MANUALLY_RECONCILED"),
  Type.Literal("PARTIALLY_REFUNDED"),
  Type.Literal("REFUNDED"),
]);
export type ConfirmedReceiptPaymentStatus = Static<typeof ConfirmedReceiptPaymentStatusSchema>;

export const ConfirmedReceiptConfirmationSourceSchema = Type.Union([
  Type.Literal("LOCAL_RECORD"),
  Type.Literal("STORE_CONFIRMED"),
  Type.Literal("PROVIDER_CONFIRMED"),
]);
export type ConfirmedReceiptConfirmationSource = Static<
  typeof ConfirmedReceiptConfirmationSourceSchema
>;

const CONFIRMED_RECEIPT_CONFIRMATION_SOURCE_TERMS = {
  LOCAL_RECORD: {
    label: "门店本机已登记",
    explanation: "由门店本机记录为已确认收款，仍应按交班和对账流程核对。",
  },
  STORE_CONFIRMED: {
    label: "门店业务账本已确认",
    explanation: "已经取得门店业务账本的确认结果。",
  },
  PROVIDER_CONFIRMED: {
    label: "支付渠道已确认",
    explanation: "已经取得支付渠道的成功结果。",
  },
} as const satisfies Record<
  ConfirmedReceiptConfirmationSource,
  { label: string; explanation: string }
>;

export function confirmedReceiptConfirmationSourceTerm(
  source: ConfirmedReceiptConfirmationSource,
): { label: string; explanation: string } {
  return CONFIRMED_RECEIPT_CONFIRMATION_SOURCE_TERMS[source];
}

const ReceiptEvidenceSchema = Type.Union([
  Type.Literal("MANUAL_FALLBACK"),
  Type.Literal("STORE_ACCEPTED"),
  Type.Literal("PROVIDER_ACCEPTED"),
]);
type ReceiptEvidence = Static<typeof ReceiptEvidenceSchema>;

const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;
const STABLE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;

function isCanonicalDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    /^\d{4}-\d{2}-\d{2}$/u.test(value) &&
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function fortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveConfirmedReceiptNumber(
  businessDate: string,
  paymentId: string,
): ConfirmedReceiptNumber {
  if (!isCanonicalDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!STABLE_ID_PATTERN.test(paymentId)) {
    throw new TypeError("paymentId must contain at least 12 safe characters");
  }
  return `SK-${businessDate.replaceAll("-", "")}-${fortyEightBitHex(
    `SK\0${businessDate}\0${paymentId}`,
  )}`;
}

export function isConfirmedReceiptNumber(value: unknown): value is ConfirmedReceiptNumber {
  if (!Value.Check(ConfirmedReceiptNumberSchema, value)) return false;
  const match = /^SK-(\d{4})(\d{2})(\d{2})-[0-9A-F]{12}$/u.exec(value);
  return Boolean(match && isCanonicalDate(`${match[1]}-${match[2]}-${match[3]}`));
}

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const PositiveMoneyFenSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const SafeMoneyFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const SharedItemProperties = {
  receiptNumber: ConfirmedReceiptNumberSchema,
  orderNumber: OrderNumberSchema,
  orderBusinessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
  tableNumber: Type.String({ minLength: 1, maxLength: 40 }),
  paymentMethod: PaidSaleReturnPaymentMethodSchema,
  paymentStatus: ConfirmedReceiptPaymentStatusSchema,
  receivedAmountFen: PositiveMoneyFenSchema,
  lifetimeRefundedFen: SafeMoneyFenSchema,
  retainedAmountFen: SafeMoneyFenSchema,
  recordedAt: Type.String({ minLength: 1, maxLength: 64 }),
} as const;

export const ConfirmedReceiptBusinessMetricDetailSourceItemSchema = Type.Object(
  {
    paymentId: StableIdSchema,
    orderId: StableIdSchema,
    ...SharedItemProperties,
    evidence: ReceiptEvidenceSchema,
    latestRefundAt: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type ConfirmedReceiptBusinessMetricDetailSourceItem = Static<
  typeof ConfirmedReceiptBusinessMetricDetailSourceItemSchema
>;

export const ConfirmedReceiptBusinessMetricDetailItemSchema = Type.Object(
  {
    ...SharedItemProperties,
    confirmationSource: ConfirmedReceiptConfirmationSourceSchema,
  },
  { additionalProperties: false },
);
export type ConfirmedReceiptBusinessMetricDetailItem = Static<
  typeof ConfirmedReceiptBusinessMetricDetailItemSchema
>;

const EnvelopeProperties = {
  contractVersion: Type.Literal(CONFIRMED_RECEIPT_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION),
  tenantId: Type.String({ minLength: 1, maxLength: 128 }),
  storeId: Type.String({ minLength: 1, maxLength: 128 }),
  businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
  timeZone: Type.String({ minLength: 1, maxLength: 80 }),
  businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
  rangeStartedAt: Type.String({ minLength: 1, maxLength: 64 }),
  rangeEndedAt: Type.String({ minLength: 1, maxLength: 64 }),
  generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
  coverage: Type.Literal("COMPLETE_CONFIRMED_RECEIPT_LEDGER"),
  valueFen: SafeMoneyFenSchema,
  factCount: Type.Integer({
    minimum: 0,
    maximum: CONFIRMED_RECEIPT_BUSINESS_METRIC_DETAIL_CAPACITY,
  }),
} as const;

export const ConfirmedReceiptBusinessMetricDetailSnapshotSchema = Type.Object(
  {
    ...EnvelopeProperties,
    items: Type.Array(ConfirmedReceiptBusinessMetricDetailSourceItemSchema, {
      maxItems: CONFIRMED_RECEIPT_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type ConfirmedReceiptBusinessMetricDetailSnapshot = Static<
  typeof ConfirmedReceiptBusinessMetricDetailSnapshotSchema
>;

export const ConfirmedReceiptBusinessMetricDetailReadModelSchema = Type.Object(
  {
    ...EnvelopeProperties,
    items: Type.Array(ConfirmedReceiptBusinessMetricDetailItemSchema, {
      maxItems: CONFIRMED_RECEIPT_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type ConfirmedReceiptBusinessMetricDetailReadModel = Static<
  typeof ConfirmedReceiptBusinessMetricDetailReadModelSchema
>;

type DetailEnvelope = Pick<
  ConfirmedReceiptBusinessMetricDetailReadModel,
  | "tenantId"
  | "storeId"
  | "businessDate"
  | "timeZone"
  | "businessDayCutover"
  | "rangeStartedAt"
  | "rangeEndedAt"
  | "generatedAt"
  | "valueFen"
  | "factCount"
>;

type SharedItem = {
  receiptNumber: ConfirmedReceiptNumber;
  orderNumber: OrderNumber;
  orderBusinessDate: string;
  tableNumber: string;
  paymentMethod: PaidSaleReturnPaymentMethod;
  paymentStatus: ConfirmedReceiptPaymentStatus;
  receivedAmountFen: number;
  lifetimeRefundedFen: number;
  retainedAmountFen: number;
  recordedAt: string;
};

function envelopeIsConsistent(value: DetailEnvelope): boolean {
  if (
    !isCanonicalDate(value.businessDate) ||
    !isCanonicalTimestamp(value.rangeStartedAt) ||
    !isCanonicalTimestamp(value.rangeEndedAt) ||
    !isCanonicalTimestamp(value.generatedAt) ||
    Date.parse(value.rangeEndedAt) <= Date.parse(value.rangeStartedAt) ||
    (value.factCount === 0) !== (value.valueFen === 0)
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: value.timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  return true;
}

function paymentRefundStateIsConsistent(item: SharedItem): boolean {
  if (
    item.lifetimeRefundedFen > item.receivedAmountFen ||
    item.retainedAmountFen !== item.receivedAmountFen - item.lifetimeRefundedFen
  ) {
    return false;
  }
  if (item.paymentStatus === "REFUNDED") {
    return item.lifetimeRefundedFen === item.receivedAmountFen;
  }
  if (item.paymentStatus === "PARTIALLY_REFUNDED") {
    return item.lifetimeRefundedFen > 0 && item.lifetimeRefundedFen < item.receivedAmountFen;
  }
  return item.lifetimeRefundedFen === 0;
}

function itemIsConsistent(item: SharedItem, envelope: DetailEnvelope): boolean {
  const recordedAt = Date.parse(item.recordedAt);
  return (
    isConfirmedReceiptNumber(item.receiptNumber) &&
    item.receiptNumber.slice(3, 11) === envelope.businessDate.replaceAll("-", "") &&
    isCanonicalDate(item.orderBusinessDate) &&
    isCanonicalTimestamp(item.recordedAt) &&
    recordedAt >= Date.parse(envelope.rangeStartedAt) &&
    recordedAt < Date.parse(envelope.rangeEndedAt) &&
    recordedAt <= Date.parse(envelope.generatedAt) &&
    paymentRefundStateIsConsistent(item) &&
    item.tableNumber === item.tableNumber.trim()
  );
}

function itemsAreConsistent(items: readonly SharedItem[], envelope: DetailEnvelope): boolean {
  if (items.length !== envelope.factCount) return false;
  const receiptNumbers = new Set<string>();
  let valueFen = 0;
  let previousRecordedAt = Number.POSITIVE_INFINITY;
  let previousReceiptNumber = "";
  for (const item of items) {
    const recordedAt = Date.parse(item.recordedAt);
    if (
      receiptNumbers.has(item.receiptNumber) ||
      !itemIsConsistent(item, envelope) ||
      recordedAt > previousRecordedAt ||
      (recordedAt === previousRecordedAt &&
        previousReceiptNumber !== "" &&
        item.receiptNumber <= previousReceiptNumber)
    ) {
      return false;
    }
    valueFen += item.receivedAmountFen;
    if (!Number.isSafeInteger(valueFen)) return false;
    receiptNumbers.add(item.receiptNumber);
    previousRecordedAt = recordedAt;
    previousReceiptNumber = item.receiptNumber;
  }
  return valueFen === envelope.valueFen;
}

function confirmationSourceForEvidence(
  evidence: ReceiptEvidence,
): ConfirmedReceiptConfirmationSource {
  if (evidence === "PROVIDER_ACCEPTED") return "PROVIDER_CONFIRMED";
  if (evidence === "STORE_ACCEPTED") return "STORE_CONFIRMED";
  return "LOCAL_RECORD";
}

function paymentMethodEvidenceIsConsistent(
  method: PaidSaleReturnPaymentMethod,
  evidence: ReceiptEvidence,
): boolean {
  const providerMethod = method === "WECHAT_PAY" || method === "ALIPAY";
  return providerMethod === (evidence === "PROVIDER_ACCEPTED");
}

function providerPaymentStatusIsConsistent(
  method: PaidSaleReturnPaymentMethod,
  status: ConfirmedReceiptPaymentStatus,
): boolean {
  return (
    (method !== "WECHAT_PAY" && method !== "ALIPAY") ||
    status === "CONFIRMED_CLOUD" ||
    status === "PARTIALLY_REFUNDED" ||
    status === "REFUNDED"
  );
}

export function isConfirmedReceiptBusinessMetricDetailSnapshot(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is ConfirmedReceiptBusinessMetricDetailSnapshot {
  if (
    !Value.Check(ConfirmedReceiptBusinessMetricDetailSnapshotSchema, value) ||
    !envelopeIsConsistent(value) ||
    (expectedScope !== undefined &&
      (value.tenantId !== expectedScope.tenantId ||
        value.storeId !== expectedScope.storeId ||
        value.businessDate !== expectedScope.businessDate))
  ) {
    return false;
  }
  const paymentIds = new Set<string>();
  for (const item of value.items) {
    let expectedReceiptNumber: ConfirmedReceiptNumber;
    try {
      expectedReceiptNumber = deriveConfirmedReceiptNumber(value.businessDate, item.paymentId);
    } catch {
      return false;
    }
    if (
      paymentIds.has(item.paymentId) ||
      item.receiptNumber !== expectedReceiptNumber ||
      !isOrderNumberForOrderId(item.orderNumber, item.orderId, item.orderBusinessDate) ||
      !paymentMethodEvidenceIsConsistent(item.paymentMethod, item.evidence) ||
      !providerPaymentStatusIsConsistent(item.paymentMethod, item.paymentStatus) ||
      (item.latestRefundAt === null) !== (item.lifetimeRefundedFen === 0) ||
      (item.latestRefundAt !== null &&
        (!isCanonicalTimestamp(item.latestRefundAt) ||
          Date.parse(item.latestRefundAt) < Date.parse(item.recordedAt) ||
          Date.parse(item.latestRefundAt) > Date.parse(value.generatedAt)))
    ) {
      return false;
    }
    paymentIds.add(item.paymentId);
  }
  return itemsAreConsistent(value.items, value);
}

export function isConfirmedReceiptBusinessMetricDetailReadModel(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is ConfirmedReceiptBusinessMetricDetailReadModel {
  return (
    Value.Check(ConfirmedReceiptBusinessMetricDetailReadModelSchema, value) &&
    envelopeIsConsistent(value) &&
    (expectedScope === undefined ||
      (value.tenantId === expectedScope.tenantId &&
        value.storeId === expectedScope.storeId &&
        value.businessDate === expectedScope.businessDate)) &&
    value.items.every(
      (item) =>
        providerPaymentStatusIsConsistent(item.paymentMethod, item.paymentStatus) &&
        (item.paymentMethod === "WECHAT_PAY" || item.paymentMethod === "ALIPAY") ===
          (item.confirmationSource === "PROVIDER_CONFIRMED"),
    ) &&
    itemsAreConsistent(value.items, value)
  );
}

export function confirmedReceiptConfirmationSourceForEvidence(
  evidence: ReceiptEvidence,
): ConfirmedReceiptConfirmationSource {
  return confirmationSourceForEvidence(evidence);
}
